import { HStack, Spacer, Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';

import ViewLayoutBlock from '@/components/view-editor/ViewLayoutBlock';
import ViewLayoutDisplayRange from '@/components/view-editor/ViewLayoutDisplayRange';
import ViewLayoutGroupBy from '@/components/view-editor/ViewLayoutGroupBy';
import { FEATURE_FLAGS_ENUM } from '@/constants/config';
import { DEFAULT_DISPLAY_SHIFT, EMPTY_ARRAY_LENGTH, SHOW_SECTIONS_REQUIRED_ERROR_MESSAGE } from '@/constants/defaults';
import { ViewEditorDrawerMode } from '@/constants/enums';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setViewEditorDraftThemeDataLayout,
  setViewEditorDraftThemeDataLayoutLeftColumnType,
  setViewEditorDraftThemeDataLayoutRange,
  setViewEditorDraftThemeDataShiftsToDisplay,
} from '@/store/slices/viewEditor.slice';
import { ShiftToDisplay, ViewLayoutType } from '@/types/view.types';

import ViewLayoutList from './ViewLayoutList';

enum ViewLayoutTabIndices {
  Standard,
  Columns,
  Calendar,
  Gantt,
  List,
  Block,
}

const selectedTabStyle = {
  color: 'white',
};

const ViewLayoutTypeTabIndices = Object.values(ViewLayoutType);

const ViewLayout = (): React.JSX.Element => {
  const { viewDraft: view, uiState } = useAppSelector((state) => state.viewEditor);
  const isCreateMode = uiState.mode === ViewEditorDrawerMode.CREATE;

  const dispatch = useAppDispatch();
  const flags = useFlags();

  const shiftsToDisplayFeatureEnabled = flags[FEATURE_FLAGS_ENUM.LV14123DailyListViewHideSections];

  const displayRange = view?.theme?.data?.range ?? 'month';
  const groupBy = view?.theme?.data?.GridSettings_leftColumnType ?? 'assignment';

  const shiftsToDisplay = React.useMemo(
    () => view?.theme?.data?.shiftsToDisplay ?? DEFAULT_DISPLAY_SHIFT,
    [view?.theme?.data?.shiftsToDisplay],
  );

  const shiftsToDisplayError =
    shiftsToDisplay.length === EMPTY_ARRAY_LENGTH ? SHOW_SECTIONS_REQUIRED_ERROR_MESSAGE : undefined;

  const tabSelectorIndex = ViewLayoutTypeTabIndices.includes(view.theme.data.layout)
    ? ViewLayoutTypeTabIndices.indexOf(view.theme.data.layout ?? ViewLayoutTabIndices.Standard)
    : ViewLayoutTabIndices.Standard;

  const handleDisplayRangeChange = (value: string) => dispatch(setViewEditorDraftThemeDataLayoutRange(value));

  const handleGroupByChange = (value: string) => dispatch(setViewEditorDraftThemeDataLayoutLeftColumnType(value));

  const handleShiftsToDisplayChange = (value: ShiftToDisplay[]) => {
    dispatch(setViewEditorDraftThemeDataShiftsToDisplay(value));
  };

  const updateShiftsToDisplayOnLayoutChange = (layout: ViewLayoutType) => {
    // Set shiftsToDisplay based on layout type and create mode (only if feature flag is enabled)
    if (shiftsToDisplayFeatureEnabled && isCreateMode) {
      if (layout === ViewLayoutType.LIST) {
        // When switching to LIST layout, initialize with default sections if not already set
        if (!view?.theme?.data?.shiftsToDisplay || view.theme.data.shiftsToDisplay.length === EMPTY_ARRAY_LENGTH) {
          dispatch(setViewEditorDraftThemeDataShiftsToDisplay(DEFAULT_DISPLAY_SHIFT));
        }
      } else {
        // When switching away from LIST layout, clear shiftsToDisplay
        dispatch(setViewEditorDraftThemeDataShiftsToDisplay(null));
      }
    }
  };

  const handleTabSelectorChange = (index: number) => {
    const layout = ViewLayoutTypeTabIndices[index] ?? ViewLayoutType.STANDARD;
    let range = displayRange;

    // Handle applying the correct views range for the selected layout
    switch (layout) {
      case ViewLayoutType.STANDARD:
      case ViewLayoutType.COLUMNS:
      case ViewLayoutType.CALENDAR:
        range = 'month';
        break;
      case ViewLayoutType.GANTT:
      case ViewLayoutType.LIST:
        range = 'day';
        break;
      case ViewLayoutType.BLOCK:
        range = 'anchored';
    }

    dispatch(setViewEditorDraftThemeDataLayout(layout));
    dispatch(setViewEditorDraftThemeDataLayoutRange(range));

    updateShiftsToDisplayOnLayoutChange(layout);
  };

  return (
    <VStack align={'left'}>
      <Text>View Layout</Text>
      <Tabs variant={'unstyled'} position={'relative'} index={tabSelectorIndex} onChange={handleTabSelectorChange}>
        <TabList>
          <Tab _selected={selectedTabStyle}>Standard</Tab>
          <Tab _selected={selectedTabStyle}>Columns</Tab>
          <Tab _selected={selectedTabStyle}>Calendar</Tab>
          <Tab _selected={selectedTabStyle}>Gantt</Tab>
          <Tab _selected={selectedTabStyle}>List</Tab>
          <Tab _selected={selectedTabStyle}>Block</Tab>
        </TabList>
        <TabIndicator
          bg={'blue.500'}
          borderRadius={'10px'}
          height={'40px'}
          mt={'-40px'}
          zIndex={-1}
          position={'inherit'}
          textColor={'white'}
        />
        <Spacer h={'10px'} borderBottom={'1px solid'} color={'blue.500'} opacity={'0.4'} />
        <TabPanels>
          {/* Standard */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.Standard && (
              <HStack w={'400px'} alignItems={'space-between'} gap={10}>
                <ViewLayoutDisplayRange
                  onChange={handleDisplayRangeChange}
                  options={[
                    { label: 'Month', value: 'month' },
                    { label: 'Week', value: 'week' },
                  ]}
                  selected={displayRange}
                />
                <ViewLayoutGroupBy onChange={handleGroupByChange} selected={groupBy} />
              </HStack>
            )}
          </TabPanel>
          {/* Columns */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.Columns && (
              <ViewLayoutGroupBy onChange={handleGroupByChange} selected={groupBy} />
            )}
          </TabPanel>
          {/* Calendar */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.Calendar && (
              <ViewLayoutGroupBy onChange={handleGroupByChange} selected={groupBy} />
            )}
          </TabPanel>
          {/* Gantt */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.Gantt && (
              <ViewLayoutGroupBy onChange={handleGroupByChange} selected={groupBy} />
            )}
          </TabPanel>
          {/* List */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.List && (
              <ViewLayoutList
                displayRange={displayRange}
                shiftsToDisplay={shiftsToDisplay}
                shiftsToDisplayError={shiftsToDisplayError}
                groupBy={groupBy}
                handleDisplayRangeChange={handleDisplayRangeChange}
                handleShiftsToDisplayChange={handleShiftsToDisplayChange}
                handleGroupByChange={handleGroupByChange}
              />
            )}
          </TabPanel>
          {/* Block */}
          <TabPanel>
            {tabSelectorIndex === ViewLayoutTabIndices.Block && (
              <ViewLayoutBlock
                displayRange={displayRange}
                groupBy={groupBy}
                handleDisplayRangeChange={handleDisplayRangeChange}
                handleGroupByChange={handleGroupByChange}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default ViewLayout;
